<template>
  <div
    ref="container"
    class="relative inline-flex type-headline-sm desk:type-headline-xs h-24 items-end gap-12 desk:h-full cursor-pointer select-none"
    :class="textClass"
    @click="expanded = !expanded"
  >
    <!-- Current selection display -->
    <div
      class="border-b flex flex-row items-center gap-8 transition-all"
      :class="{
        'border-border-strong': expanded,
        'border-transparent': !expanded,
        'pb-4 desk:pb-12': !footerStyling
      }"
    >
      <div class="w-20 h-16 flex items-center justify-center">
        <img
          :src="currentLanguage.flagUrl"
          :alt="currentLanguage.label"
          class="object-contain rounded-2"
        >
      </div>
      <span>{{ currentLanguage.countryCode }}/{{ currentLanguage.currency }}</span>
    </div>

    <!-- Dropdown menu -->
    <div
      v-if="expanded"
      class="absolute bottom-full mb-8 desk:bottom-auto desk:top-full desk:mt-8 bg-lightest outline outline-1 outline-bg-inverse/10 shadow-lg z-modals min-w-[280px] max-h-[80vh] desk:max-h-[400px] overflow-y-auto py-6 right-0"
      :class="{
        'bottom-32 left-1/2 transform -translate-x-1/2 !top-auto': footerStyling
      }"
    >
      <a
        v-for="lang in languages"
        :key="lang.url"
        :href="lang.url"
        class="flex items-center gap-8 px-16 py-8 hover:bg-bg-hover transition-colors type-sm"
        :class="{
          'bg-bg-selected': lang.url === currentLanguage?.url
        }"
        @click.stop="navigateToLanguage(lang.url)"
      >
        <div class="rounded-2 overflow-hidden w-16 h-12 shrink-0">
          <img
            :src="lang.flagUrl"
            :alt="lang.label"
            class="w-full h-full object-contain"
          >
        </div>
        <span class="type-sm">{{ lang.label }}</span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { useGlobalContentStore } from '~/store/globalContent';

const globalContent = useGlobalContentStore();
const { $t } = useNuxtApp();
const expanded = ref(false);
const container = useTemplateRef('container');

onClickOutside(container, () => {
  expanded.value = false;
});

const props = defineProps({
  invertTextColor: {
    type: Boolean,
    default: null,
  },
  footerStyling: Boolean,
});

const textClass = computed(() => {
  if (props.invertTextColor === null) {
    return '';
  }
  return props.invertTextColor ? 'text-text-inverse' : 'text-text-base';
});

type LanguageOption = {
  label: string;
  url: string;
  countryCode: string;
  flagUrl: string;
}

const languages = computed<LanguageOption[]>(() => {
  const marketKeys = ['at', 'ch_fr', 'ch_de', 'de', 'dk', 'en', 'fi', 'fr', 'no', 'se', 'gb', 'us'];

  const flagMap: Record<string, string> = {
    at: '/flags/AT.svg',
    ch_fr: '/flags/CH.svg',
    ch_de: '/flags/CH.svg',
    de: '/flags/DE.svg',
    dk: '/flags/DK.svg',
    en: '/flags/global-world.jpg',
    fi: '/flags/FI.svg',
    fr: '/flags/FR.svg',
    no: '/flags/NO.svg',
    se: '/flags/SE.svg',
    gb: '/flags/GB-UKM.svg',
    us: '/flags/US.svg',
  };

  const allMarkets: LanguageOption[] = marketKeys.map(key => {
    const countryCode = key.toUpperCase();
    const label = $t(`lang.selector.label.${key}`);
    const url = $t(`lang.selector.url.${key}`);

    return {
      label,
      url,
      countryCode,
      flagUrl: flagMap[key] || '/flags/global-world.jpg',
    };
  });

  return allMarkets;
});

const currentLanguage = computed(() => {
  const currentCountryCode = globalContent.getCountryCode?.toUpperCase() || '';
  const currentCurrency = globalContent.getCurrency?.toUpperCase() || 'EUR';

  const found = languages.value.find(lang => {
    const langCountryCode = lang.countryCode.split('_')[0];
    return langCountryCode === currentCountryCode;
  });

  if (found) {
    return {
      ...found,
      currency: currentCurrency,
    };
  }

  return {
    label: `${currentCountryCode}/${currentCurrency}`,
    url: 'https://www.kahrs.com/en/',
    countryCode: currentCountryCode,
    currency: currentCurrency,
    flagUrl: '/flags/global-world.jpg',
  };
});

const navigateToLanguage = (url: string) => {
  window.location.href = url;
};

</script>
